import React, { useEffect, useMemo, useState } from 'react';

import { listCollaborators, type CollaboratorRow } from './api';

/**
 * The prospect list: every workspace that signed up through an invite to work (a
 * contractor, a requester, a report recipient), who brought it, how much it does on
 * others' work, and whether it has since chosen a plan of its own. Read from the
 * record (admin_list_collaborators), not analytics, so nobody is missing for having
 * declined analytics.
 */
const PERSONA: Record<string, string> = {
  contractor: 'Contractors',
  requester: 'Requesters',
  recipient: 'Report reviewers',
};

const date = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');
const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '—');

export default function Collaborators({ toast }: { toast: (message: string) => void }) {
  const [rows, setRows] = useState<CollaboratorRow[] | null>(null);

  useEffect(() => {
    listCollaborators()
      .then(setRows)
      .catch((e: Error) => {
        toast(`Could not read collaborators: ${e.message}`);
        setRows([]);
      });
  }, [toast]);

  const converted = (r: CollaboratorRow) => r.workspace_mode === 'manager';

  const byPersona = useMemo(() => {
    const out = new Map<string, { total: number; converted: number }>();
    for (const r of rows ?? []) {
      const k = r.joined_as ?? 'other';
      const v = out.get(k) ?? { total: 0, converted: 0 };
      v.total += 1;
      if (converted(r)) v.converted += 1;
      out.set(k, v);
    }
    return [...out.entries()].sort((a, b) => b[1].total - a[1].total);
  }, [rows]);

  const byHost = useMemo(() => {
    const out = new Map<string, { name: string; total: number; converted: number }>();
    for (const r of rows ?? []) {
      const k = r.invited_by_account_id ?? 'unknown';
      const v = out.get(k) ?? { name: r.invited_by_name ?? 'Unknown workspace', total: 0, converted: 0 };
      v.total += 1;
      if (converted(r)) v.converted += 1;
      out.set(k, v);
    }
    return [...out.values()].sort((a, b) => b.total - a.total).slice(0, 15);
  }, [rows]);

  if (!rows) return <p className="muted">Loading…</p>;
  if (!rows.length) return <p className="sub">No one has signed up through an invite to work yet.</p>;

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <section className="card">
        <h2 style={{ marginTop: 0 }}>Conversion by how they arrived</h2>
        <table className="matrix" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Arrived as</th>
              <th className="col-ed">People</th>
              <th className="col-ed">Chose a plan</th>
              <th className="col-ed">Rate</th>
            </tr>
          </thead>
          <tbody>
            {byPersona.map(([k, v]) => (
              <tr key={k}>
                <td>{PERSONA[k] ?? k}</td>
                <td className="col-ed">{v.total}</td>
                <td className="col-ed">{v.converted}</td>
                <td className="col-ed">{pct(v.converted, v.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card">
        <h2 style={{ marginTop: 0 }}>Conversion by the workspace that brought them</h2>
        <table className="matrix" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Workspace</th>
              <th className="col-ed">Brought</th>
              <th className="col-ed">Chose a plan</th>
              <th className="col-ed">Rate</th>
            </tr>
          </thead>
          <tbody>
            {byHost.map((v) => (
              <tr key={v.name}>
                <td>{v.name}</td>
                <td className="col-ed">{v.total}</td>
                <td className="col-ed">{v.converted}</td>
                <td className="col-ed">{pct(v.converted, v.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card">
        <h2 style={{ marginTop: 0 }}>Everyone</h2>
        <p className="sub">Newest first. &ldquo;Chose a plan&rdquo; means they now run a workspace of their own.</p>
        <table className="matrix" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Person</th>
              <th>Arrived as</th>
              <th>Brought by</th>
              <th className="col-ed">Joined</th>
              <th className="col-ed">Last active</th>
              <th className="col-ed">Jobs done</th>
              <th className="col-ed">Requests</th>
              <th className="col-ed">Reports reviewed</th>
              <th>Plan</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.account_id}>
                <td>{r.email ?? r.account_name}</td>
                <td>{PERSONA[r.joined_as ?? ''] ?? r.joined_as ?? '—'}</td>
                <td>{r.invited_by_name ?? '—'}</td>
                <td className="col-ed">{date(r.joined_at)}</td>
                <td className="col-ed">{date(r.last_active_at)}</td>
                <td className="col-ed">{r.jobs_done}</td>
                <td className="col-ed">{r.requests_raised}</td>
                <td className="col-ed">{r.reports_reviewed}</td>
                <td>{converted(r) ? `Chose ${r.edition_key ?? 'a plan'}` : 'Still a collaborator'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
